import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../../pages/Personal Attendance/useAuth";
import { WORKFLOWS } from "./MenuHub";

/**
 * CommandPalette — Round 25. Press Ctrl+K (or Cmd+K) anywhere, type a few
 * letters, Enter — jump straight to any workflow, sub-action, department,
 * or HR workspace. The topbar search button dispatches `ssw:openPalette`.
 *
 * Permission model: entries are filtered with the SAME gates the launcher
 * and sidebar use (module gates via canAccessModule, manager/HR/IT flags),
 * so the palette can never reveal a destination the user couldn't reach
 * through the UI. Workflow entries come straight from MenuHub's catalog
 * (single source of truth); department entries mirror LayoutMenuData's
 * gates and are documented as such.
 *
 * Pure frontend: navigation only, no API calls.
 */

// Departments — labels/links/gates mirror Layouts/LayoutMenuData.js.
const DEPARTMENTS = [
    { label: "Engineering",            to: "/engineering",   gate: "mgr" },
    { label: "Planning",               to: "/planning",      gate: "mgr" },
    { label: "Production",             to: "/production",    gate: "mgr" },
    { label: "Quality",                to: "/quality",       gate: "mgr" },
    { label: "Shipping",               to: "/shipping",      gate: "mgr" },
    { label: "Accounting",             to: "/accounting",    gate: "mgrHr" },
    { label: "Sales",                  to: "/sales",         gate: "mgr" },
    { label: "Shukla",                 to: "/shukla",        gate: "mgr" },
    { label: "Human Resource",         to: "/humanresource", gate: "hr" },
    { label: "HR Dashboard",           to: "/hr/dashboard",  gate: "hr" },
    { label: "My Profile",             to: "/profile/me" },
    { label: "Information Technology", to: "/it",            gate: "it" },
    { label: "Supervisor",             to: "/supervisor",    gate: "mgrSu" },
    { label: "KPIs",                   to: "/kpi",           gate: "mgrSu" },
];

const HR_WORKSPACES = [
    { label: "Employee management", to: "/humanresource?tab=1", gate: "hr" },
    { label: "HR reports",          to: "/humanresource?tab=2", gate: "hr" },
    { label: "Punch editor / Missing punches", to: "/humanresource?tab=4", gate: "hr" },
    { label: "Web forms",           to: "/humanresource?tab=5", gate: "hr" },
];

const APPS = [
    { label: "MAFIA Aux",      to: "/mafia", module: "app.mafia" },
    { label: "Support",        to: "/support", module: "support" },
    { label: "Admin tools",    to: "/admin", gate: "it" },
    { label: "Expected Visitors", to: "/visitors" },
    { label: "Dashboard home", to: "/dashboard" },
    { label: "Workflows",      to: "/workflows" },
];

const CommandPalette = () => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [cursor, setCursor] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const navigate = useNavigate();

    const auth = useAuth();
    const { isManager, isItAdmin, isHR, isSuperUser, canAccessModule } = auth;

    const gateOk = useCallback((g) => {
        if (!g) return true;
        if (g === "mgr")   return !!(isManager || isItAdmin);
        if (g === "mgrHr") return !!(isManager || isHR || isItAdmin);
        if (g === "mgrSu") return !!(isManager || isSuperUser || isItAdmin);
        if (g === "hr")    return !!(isHR || isItAdmin);
        if (g === "it")    return !!isItAdmin;
        return true;
    }, [isManager, isItAdmin, isHR, isSuperUser]);

    const wfVisible = useCallback((e) => {
        if (e.module) return canAccessModule(e.module);
        if (e.requiresManagerOrAdmin) return !!(isManager || isItAdmin || isSuperUser || isHR);
        return true;
        // eslint pacification not needed: plain function
    }, [canAccessModule, isManager, isItAdmin, isSuperUser, isHR]);

    // Build the catalog once per auth change: workflows + flattened
    // sub-actions ("EPO › Ready for Payment"), departments, HR, apps.
    const catalog = useMemo(() => {
        const out = [];
        WORKFLOWS.forEach((wf) => {
            if (!wfVisible(wf)) return;
            const subs = (wf.subActions || []).filter(wfVisible);
            if (subs.length > 0) {
                out.push({ label: wf.label, hint: "Workflow hub", to: `/workflows?wf=${wf.key}`, icon: wf.icon });
                subs.forEach((sa) =>
                    out.push({ label: `${wf.label} › ${sa.label}`, hint: sa.subtitle, to: sa.to, icon: sa.icon })
                );
            } else {
                out.push({ label: wf.label, hint: wf.subtitle, to: wf.to, icon: wf.icon });
            }
        });
        DEPARTMENTS.forEach((d) => gateOk(d.gate) && out.push({ label: d.label, hint: "Department", to: d.to, icon: "ri-building-line" }));
        HR_WORKSPACES.forEach((h) => gateOk(h.gate) && out.push({ label: `HR › ${h.label}`, hint: "HR workspace", to: h.to, icon: "ri-team-line" }));
        APPS.forEach((a) => {
            if (a.module ? canAccessModule(a.module) : gateOk(a.gate)) {
                out.push({ label: a.label, hint: "App", to: a.to, icon: "ri-apps-2-line" });
            }
        });
        return out;
    }, [wfVisible, gateOk, canAccessModule]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return catalog.slice(0, 9);
        return catalog
            .filter((e) => e.label.toLowerCase().includes(q) || (e.hint || "").toLowerCase().includes(q))
            .slice(0, 9);
    }, [catalog, query]);

    const close = useCallback(() => { setOpen(false); setQuery(""); setCursor(0); }, []);

    const go = useCallback((entry) => {
        if (!entry) return;
        close();
        navigate(entry.to);
    }, [navigate, close]);

    // Global listeners: Ctrl/Cmd+K toggles; topbar button dispatches event.
    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === "k") {
                e.preventDefault();
                setOpen((v) => !v);
            } else if (e.key === "Escape") {
                setOpen(false);
            }
        };
        const onOpenEvt = () => setOpen(true);
        window.addEventListener("keydown", onKey);
        window.addEventListener("ssw:openPalette", onOpenEvt);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("ssw:openPalette", onOpenEvt);
        };
    }, []);

    useEffect(() => { if (open) setTimeout(() => inputRef.current && inputRef.current.focus(), 30); }, [open]);
    useEffect(() => { setCursor(0); }, [query]);

    if (!open) return null;

    const onInputKeyDown = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); go(results[cursor]); }
    };

    return (
        <div
            onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
            style={{
                position: "fixed", inset: 0, zIndex: 4000,
                background: "rgba(10, 12, 20, 0.45)",
                display: "flex", alignItems: "flex-start", justifyContent: "center",
                paddingTop: "12vh",
            }}
            role="dialog" aria-modal="true" aria-label="Quick navigation"
        >
            <div
                style={{
                    width: "min(640px, 92vw)",
                    background: "var(--ssw-surface, #fff)",
                    border: "1px solid var(--ssw-border, #e9ecf3)",
                    borderRadius: 14,
                    boxShadow: "0 24px 64px rgba(8, 10, 20, 0.35)",
                    overflow: "hidden",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--ssw-border, #e9ecf3)" }}>
                    <i className="ri-search-line" style={{ fontSize: 18, color: "var(--ssw-text-2, #66708a)" }} />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onInputKeyDown}
                        placeholder="Jump to… (try 'punch', 'travel', 'audit')"
                        aria-label="Search destinations"
                        style={{
                            flex: 1, border: "none", outline: "none", background: "transparent",
                            fontSize: 15.5, color: "var(--ssw-text-1, #1c2333)",
                        }}
                    />
                    <span style={{ fontSize: 11, color: "var(--ssw-text-2, #66708a)", border: "1px solid var(--ssw-border, #e9ecf3)", borderRadius: 6, padding: "2px 6px" }}>Esc</span>
                </div>

                <div ref={listRef} style={{ maxHeight: 380, overflowY: "auto", padding: 6 }}>
                    {results.length === 0 ? (
                        <div style={{ padding: "18px 14px", color: "var(--ssw-text-2, #66708a)", fontSize: 13.5 }}>
                            Nothing matches “{query}”.
                        </div>
                    ) : results.map((r, idx) => (
                        <div
                            key={`${r.to}-${idx}`}
                            onMouseEnter={() => setCursor(idx)}
                            onMouseDown={(e) => { e.preventDefault(); go(r); }}
                            style={{
                                display: "flex", alignItems: "center", gap: 12,
                                padding: "10px 12px", borderRadius: 9, cursor: "pointer",
                                background: idx === cursor ? "rgba(91, 80, 214, 0.10)" : "transparent",
                            }}
                        >
                            <i className={r.icon || "ri-arrow-right-line"} aria-hidden="true"
                               style={{ fontSize: 17, color: idx === cursor ? "var(--ssw-accent-ink, #4338ca)" : "var(--ssw-text-2, #66708a)", flexShrink: 0 }} />
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ssw-text-1, #1c2333)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {r.label}
                                </div>
                                {r.hint ? (
                                    <div style={{ fontSize: 12, color: "var(--ssw-text-2, #66708a)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {r.hint}
                                    </div>
                                ) : null}
                            </div>
                            {idx === cursor ? (
                                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--ssw-text-2, #66708a)" }}>↵</span>
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CommandPalette;
